/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/kebab.svg";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {Reminder} from "@app/app-shared/reminder";
import dayjs from "../../../utils/dayjs";
import * as React from "react";
import {FONTS} from "@res/fonts";
import {RemindersModule} from "@modules/RemindersModule";
import {DB_Patient} from "@app/app-shared/patient";
import {showTooltipAtXY} from "../../widget/appTooltip";
import {COLORS} from "@res/colors";
import {EditReminderTooltip} from "./EditReminderTooltip";
import {ScheduledBy} from "@app/ir-q-app-common/types/calendar_event";
import {currentTimeMillies} from "@intuitionrobotics/ts-common";
import {PatientsContext} from "../patients/PatientsContext";


const medicationReminderBadge = {
    display: 'flex',
    flexDirection: "column" as "column",
    gap: '1vh',
    alignItems: "flex-start",
    justifyContent: "space-between",
    width: "100%"
}

type Props = {
    reminder: Reminder;
    patient: DB_Patient;
}


const kebab = __asset_0;

export class MedicationBadge
    extends BaseComponent<Props> {
    constructor(props: Props) {
        super(props);
    }

    renderStatus = () => {
        // Returns the literal we display in the badge about the reminder status
        const originator = this.props.reminder.originator;
        const status = this.props.reminder.status;

        if (originator === ScheduledBy.CARE_CENTER) {
            if (status === 'DELETED') {
                return FONTS.bold_14_secondary_red(`Deleted`);
            }

            if (status === 'CREATED') {
                return FONTS.bold_14_orange(`Pending`);
            }
        } else if (originator === ScheduledBy.USER) {
            if (status === 'DELETED') {
                return FONTS.bold_14_secondary_red(`Declined`)
            }

            if (status === 'CREATED') {
                return FONTS.bold_14_secondary_green(`Accepted`)
            }
        }

        return FONTS.bold_14_orange(`Unknown`);
    }

    renderRecurrence = () => {
        const recurrence = this.props.reminder.recurring;
        if (!recurrence) {
            // if the event is not recurring display the date of the one time reminder
            return FONTS.reg_14_gray(dayjs(this.props.reminder.startTime).format("MM/DD/YYYY "));
        }
        return FONTS.reg_14_gray(RemindersModule.generateRecurrenceString(recurrence));
    }


    render() {
        return (
            <div style={medicationReminderBadge}>
                <h1 style={{width: "100%", margin: "20px 0px 0px 0px"}}>
                    <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between'}}>
                        <div>
                            {FONTS.bold_14_gray_4(`${this.props.reminder.subject}`)}
                            {this.props.reminder.metadata?.medsMealRequired ? FONTS.reg_12_gray(` , Meal required`) : ''}
                        </div>
                        <div style={{display: "flex", alignItems: "center"}}>
                            {this.renderStatus()}
                            {this.canReminderBeDeleted() && <PatientsContext.Consumer>{({demo}) => !demo ? this.renderKebabMenu() : null}</PatientsContext.Consumer>}
                        </div>
                    </div>
                </h1>
                <h1 style={{margin: "2px 0px 20px 0px"}}>
                    <div>
                        {this.renderRecurrence()} {FONTS.reg_14_gray(`at ${dayjs(this.props.reminder.startTime).format('h:mmA')}`)}
                    </div>
                </h1>
            </div>
        )
    }

    private canReminderBeDeleted() {

        if (!RemindersModule.assertUserCanEditReminders(this.props.patient))
            return false;

        if (this.props.reminder.status === 'DELETED')
            return false;

        const isExpired = this.props.reminder.startTime < currentTimeMillies();
        return !isExpired || (this.props.reminder.recurring && this.props.reminder.recurring !== "");
    }

    private renderKebabMenu() {
        return <img src={kebab}
                    className={"clickable"}
                    alt="kebab"
                    style={{marginLeft: 20}}
                    height={25}
                    width={5}
                    onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        showTooltipAtXY(
                            <EditReminderTooltip patient={this.props.patient} reminder={this.props.reminder}/>,
                            rect.left - 150, rect.top - 60,
                            {
                                backgroundColor: COLORS.white,
                                padding: 8, borderRadius: 2, boxShadow: "1px 1px 6px rgba(0, 0, 0, 0.3)"
                            }, 31540000000);
                    }}
        />;
    }
}
